import React, { useCallback, useEffect, useState } from 'react';

import { fg } from '@atlassian/jira-feature-gating';

import { memoizedFetchSiteUserPersonalization } from '@atlassian/jira-personalization-service/src/services/site-user/index.tsx';

import { memoizedFetchSitePersonalization } from '@atlassian/jira-personalization-service/src/services/site/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';

import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { TryHomeDashboardsBanner } from './try-home-dashboards-banner/index.tsx';
import { ActivateHomeDashboardsBanner } from './activate-home-dashboards-banner/index.tsx';

const getTapAttributes = async (cloudId: string, userId: string) => {
	try {
		const siteUserAttributes = await memoizedFetchSiteUserPersonalization({
			cloudId,
			userId,
		});

		const siteAttributes = await memoizedFetchSitePersonalization({
			cloudId,
		});
		const isSiteUserAnOrgAdmin =
			siteUserAttributes.find((trait) => trait.name === 'is_site_user_an_org_admin')?.value ===
			true;

		const hasOptedInTownsquare =
			siteAttributes.find((trait) => trait.name === 'has_opted_in_townsquare')?.value === true;

		const hasJiraDashboardMarketplaceApps =
			siteAttributes.find((trait) => trait.name === 'has_jira_dashboard_marketplace_apps')
				?.value === true;

		return {
			isSiteUserAnOrgAdmin,
			hasOptedInTownsquare,
			hasJiraDashboardMarketplaceApps,
		};
	} catch (error: unknown) {
		if (error instanceof Error) {
			fireErrorAnalytics({
				error,
				meta: {
					id: 'engagementBannersGetTapAttributes',
					packageName: 'jiraDashboardInternalCommon',
					teamName: 'endeavour',
				},
			});
		}
		return {};
	}
};

/**
 * Shows different content based on feature flags and can be dismissed.
 */

export const EngagementBanners = () => {
	const cloudId = useCloudId();
	const userId = useAccountId();
	const [isSiteUserAnOrgAdmin, setIsSiteUserAnOrgAdmin] = useState<boolean>();
	const [hasOptedInTownsquare, setHasOptedInTownsquare] = useState<boolean>();
	const [hasJiraDashboardMarketplaceApps, setHasJiraDashboardMarketplaceApps] = useState<boolean>();

	const getTapAttributesMemoized = useCallback(async () => {
		if (!userId) return;

		const {
			isSiteUserAnOrgAdmin: isSiteUserAnOrgAdminTraitValue,
			hasOptedInTownsquare: hasOptedInTownsquareTraitValue,
			hasJiraDashboardMarketplaceApps: hasJiraDashboardMarketplaceAppsTraitValue,
		} = await getTapAttributes(cloudId, userId);

		setIsSiteUserAnOrgAdmin(isSiteUserAnOrgAdminTraitValue);
		setHasOptedInTownsquare(hasOptedInTownsquareTraitValue);
		setHasJiraDashboardMarketplaceApps(hasJiraDashboardMarketplaceAppsTraitValue);
	}, [cloudId, userId]);

	useEffect(() => {
		if (fg('endeavour_fetch_tap_traits_from_jira_dashboards')) {
			if (cloudId && userId) {
				getTapAttributesMemoized();
			}
		}
	}, [cloudId, getTapAttributesMemoized, userId]);

	return (
		<ContextualAnalyticsData sourceName="jiraDashboardsBanners" sourceType={SCREEN}>
			<>
				{isSiteUserAnOrgAdmin &&
				!hasOptedInTownsquare &&
				!hasJiraDashboardMarketplaceApps &&
				expValEquals('jira_dashboard_home_beta_opt_in', 'isBannerRenderable', true, false) ? (
					<ActivateHomeDashboardsBanner />
				) : null}
			</>
			<>
				{hasOptedInTownsquare &&
				!hasJiraDashboardMarketplaceApps &&
				expValEquals(
					'jira_dashboards_prompt_to_new_experience',
					'isBannerRenderable',
					true,
					false,
				) ? (
					<TryHomeDashboardsBanner />
				) : null}
			</>
		</ContextualAnalyticsData>
	);
};
